import { MoreHoriz } from "@mui/icons-material";
import {
  BottomNavigation,
  BottomNavigationAction,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { useIntl } from "../../contexts/IntlContext";
import { BOTTOM_NAV_PRIMARY_ROUTES, navigationOptions } from "../../constants/navigation";

const MORE_VALUE = "__more__";

export function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { authInfo } = useContext(AuthContext);
  const intl = useIntl();
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);

  const visibleItems = useMemo(
    () => navigationOptions.filter((item) => authInfo?.role && item.roles.includes(authInfo.role)),
    [authInfo?.role],
  );

  const primaryItems = useMemo(
    () => visibleItems.filter((item) => BOTTOM_NAV_PRIMARY_ROUTES.includes(item.route)),
    [visibleItems],
  );

  const moreItems = useMemo(
    () => visibleItems.filter((item) => !BOTTOM_NAV_PRIMARY_ROUTES.includes(item.route)),
    [visibleItems],
  );

  const isOnMoreItemRoute = useMemo(
    () => moreItems.some((item) => location.pathname === item.route || location.pathname.startsWith(item.route)),
    [location.pathname, moreItems],
  );

  const currentValue = useMemo(() => {
    if (isOnMoreItemRoute) return MORE_VALUE;
    return (
      primaryItems.find(
        (item) => location.pathname === item.route || location.pathname.startsWith(item.route),
      )?.route ?? false
    );
  }, [location.pathname, primaryItems, isOnMoreItemRoute]);

  if (!isMobile || visibleItems.length === 0) return null;

  return (
    <Paper
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
      }}
      elevation={3}
    >
      <BottomNavigation
        value={currentValue}
        onChange={(event, newValue) => {
          if (newValue === MORE_VALUE) {
            setMoreAnchorEl(event.currentTarget as HTMLElement);
            return;
          }
          navigate(newValue);
        }}
        showLabels
        sx={{
          "& .MuiBottomNavigationAction-root": {
            minWidth: 0,
            maxWidth: "none",
            px: 0.5,
          },
          "& .MuiBottomNavigationAction-label": {
            fontSize: "0.65rem",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
            "&.Mui-selected": {
              fontSize: "0.7rem",
            },
          },
        }}
      >
        {primaryItems.map((item) => (
          <BottomNavigationAction
            key={item.route}
            label={intl.formatMessage({ id: 'drawer.' + item.nameKey })}
            value={item.route}
            icon={item.icon}
          />
        ))}
        {moreItems.length > 0 && (
          <BottomNavigationAction
            label={intl.formatMessage({ id: 'drawer.more' })}
            value={MORE_VALUE}
            icon={<MoreHoriz />}
          />
        )}
      </BottomNavigation>

      <Menu anchorEl={moreAnchorEl} open={!!moreAnchorEl} onClose={() => setMoreAnchorEl(null)}>
        {moreItems.map((item) => (
          <MenuItem
            key={item.route}
            onClick={() => {
              setMoreAnchorEl(null);
              navigate(item.route);
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{intl.formatMessage({ id: 'drawer.' + item.nameKey })}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Paper>
  );
}
